/**
 * useWatchlist – Watchlist & Alerts store (Zustand).
 */
import { create } from 'zustand'
import {
  getWatchlist,
  addToWatchlist as apiAdd,
  removeFromWatchlist as apiRemove,
  updateWatchlistItem as apiUpdate,
  getTriggeredAlerts,
  dismissWatchlistAlert as apiDismiss,
} from '@/lib/api'
import type { WatchlistItem, AlertCondition, TriggeredAlert } from '@/lib/types'

interface WatchlistState {
  items: WatchlistItem[]
  alerts: TriggeredAlert[]
  loading: boolean
  error: string | null

  fetchWatchlist: () => Promise<void>
  addItem: (body: {
    ticker: string; name: string; notes?: string;
    alerts?: AlertCondition[];
  }) => Promise<WatchlistItem | null>
  removeItem: (id: number) => Promise<void>
  updateItem: (id: number, body: {
    notes?: string; alerts?: AlertCondition[];
  }) => Promise<void>
  fetchAlerts: () => Promise<void>
  dismissAlert: (id: number) => Promise<void>
}

export const useWatchlist = create<WatchlistState>()((set, get) => ({
  items: [],
  alerts: [],
  loading: false,
  error: null,

  fetchWatchlist: async () => {
    set({ loading: true, error: null })
    try {
      const items = await getWatchlist()
      set({ items, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  addItem: async (body) => {
    try {
      const item = await apiAdd(body)
      set(s => ({ items: [...s.items, item] }))
      return item
    } catch (e) {
      set({ error: String(e) })
      return null
    }
  },

  removeItem: async (id) => {
    try {
      await apiRemove(id)
      set(s => ({ items: s.items.filter(i => i.id !== id) }))
    } catch (e) {
      set({ error: String(e) })
    }
  },

  updateItem: async (id, body) => {
    try {
      const updated = await apiUpdate(id, body)
      set(s => ({ items: s.items.map(i => i.id === id ? updated : i) }))
    } catch (e) {
      set({ error: String(e) })
    }
  },

  fetchAlerts: async () => {
    try {
      const alerts = await getTriggeredAlerts()
      set({ alerts })
    } catch (e) {
      set({ error: String(e) })
    }
  },

  dismissAlert: async (id) => {
    try {
      await apiDismiss(id)
      set(s => ({
        alerts: s.alerts.filter(a => a.watchlist_id !== id),
        items: s.items.map(i =>
          i.id === id
            ? { ...i, alerts: i.alerts.map(a => ({ ...a, triggered: false })) }
            : i,
        ),
      }))
    } catch (e) {
      set({ error: String(e) })
    }
  },
}))
